import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ASSETS, URLS } from "@/data/constants";
import { trackCalendlyClick } from "@/utils/analytics";
import type { DeepReadonly } from "@/types";
import { MenuIcon } from "@/components/design-system/basic-icons/MenuIcon";
import { CloseIcon } from "@/components/design-system/basic-icons/CloseIcon";
import { ChevronIcon } from "@/components/design-system/basic-icons/ChevronIcon";
import { NavDropdown } from "@/components/design-system/NavDropdown/NavDropdown";
import { Button } from "@/components/design-system/Button/Button";

type NavigationProps = DeepReadonly<{
  isScrolled?: boolean;
}>;

const solutionsItems = [
  { label: "Wealth Managers", href: "/wealthmanagers" },
  { label: "Credit Unions", href: "/creditunions" },
  { label: "Employers", href: "/employers" },
] as const;


export const Navigation: React.FC<NavigationProps> = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const { pathname, hash } = location;
  const navigate = useNavigate();

  useEffect(() => {
    const scrollTarget = (location.state as { scrollTo?: string } | null)
      ?.scrollTo;
    if (scrollTarget) {
      const frame = requestAnimationFrame(() => {
        document
          .getElementById(scrollTarget)
          ?.scrollIntoView({ behavior: "smooth" });
      });
      return () => cancelAnimationFrame(frame);
    }
    if (hash) {
      const frame = requestAnimationFrame(() => {
        document
          .getElementById(hash.slice(1))
          ?.scrollIntoView({ behavior: "smooth" });
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [location.state, hash]);

  const handleHashNavClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    if (pathname === "/") {
      document
        .getElementById(sectionId)
        ?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: sectionId } });
    }
  };

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 120);
  };

  const handleLogoClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (window.location.hash) window.location.hash = "";
  };

  const handleCalendlyClick = (source: string) => {
    trackCalendlyClick(source);
    window.open(URLS.CALENDLY, "_blank", "noopener,noreferrer");
  };

  const isSolutionsActive = solutionsItems.some((i) => i.href === pathname);

  return (
    <>
      {/* ========== NAVBAR ========== */}
      <nav
        id="main-nav"
        className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-[5px]"
      >
        <div className="flex items-stretch justify-between gap-4 px-5 md:px-8 lg:px-16">
          <div className="flex items-center py-4">
            <a href="/" onClick={handleLogoClick}>
              <img src={ASSETS.BRAND.LOGO} alt="Bravo" className="h-10" />
            </a>
          </div>

          {/* Desktop nav */}
          <div className="hidden items-stretch gap-4 md:flex lg:gap-8">
            {/* Solutions with dropdown */}
            <div
              className="relative flex items-center"
              onMouseEnter={openDropdown}
              onMouseLeave={scheduleClose}
            >
              <a
                href="/#solutions"
                onClick={(e) => {
                  setDropdownOpen(false);
                  handleHashNavClick(e, "solutions");
                }}
                className={`body-b-regular transition-all hover:font-semibold ${
                  isSolutionsActive
                    ? "font-semibold text-primary-500"
                    : "text-neutral-800 hover:text-primary-500"
                }`}
              >
                Solutions
              </a>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-0.5"
                  onMouseEnter={openDropdown}
                  onMouseLeave={scheduleClose}
                >
                  <NavDropdown
                    items={solutionsItems}
                    onItemClick={() => setDropdownOpen(false)}
                  />
                </div>
              )}
            </div>

            {/* Platform */}
            <a
              href="/#platform"
              onClick={(e) => handleHashNavClick(e, "platform")}
              className="body-b-regular flex items-center text-neutral-800 transition-all hover:font-semibold hover:text-primary-500"
            >
              Platform
            </a>

            {/* How it works */}
            <a
              href="/#how-it-works"
              onClick={(e) => handleHashNavClick(e, "how-it-works")}
              className="body-b-regular flex items-center text-neutral-800 transition-all hover:font-semibold hover:text-primary-500"
            >
              How it works
            </a>

            <div className="flex items-center">
              <Button
                size="sm"
                onClick={() => handleCalendlyClick("header")}
              >
                Schedule a Demo
              </Button>
            </div>
          </div>

          {/* Hamburger / X button (mobile only) */}
          <button
            id="nav-menu-btn"
            className="flex items-center p-1 md:hidden"
            style={{ color: "#6b7280" }}
            onClick={() => {
              const next = !mobileMenuOpen;
              setMobileMenuOpen(next);
              if (!next) setMobileSolutionsOpen(false);
            }}
            aria-label="Open menu"
          >
            <MenuIcon className={mobileMenuOpen ? "hidden" : ""} />
            <CloseIcon className={mobileMenuOpen ? "" : "hidden"} />
          </button>
        </div>
      </nav>

      {/* Mobile flyout menu */}
      <div
        id="mobile-menu"
        className={`${mobileMenuOpen ? "" : "hidden"} fixed top-16 right-0 left-0 z-40 bg-white shadow-lg md:hidden`}
      >
        <div className="flex flex-col">
          {/* Solutions accordion */}
          <button
            className="flex items-center justify-between px-5 py-3 text-left transition-colors hover:bg-neutral-100"
            onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
          >
            <span
              className={`${mobileSolutionsOpen ? "body-a-semibold" : "body-a-regular"} text-neutral-800`}
            >
              Solutions
            </span>
            <ChevronIcon
              className={`text-neutral-800 transition-transform ${mobileSolutionsOpen ? "rotate-180" : ""}`}
            />
          </button>

          {mobileSolutionsOpen && (
            <div className="bg-neutral-100">
              {solutionsItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => {
                    setMobileSolutionsOpen(false);
                    setMobileMenuOpen(false);
                  }}
                  className="body-a-regular block px-11 py-3 text-neutral-800 transition-colors hover:bg-neutral-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}

          {/* Platform */}
          <a
            href="/#platform"
            onClick={(e) => {
              setMobileMenuOpen(false);
              handleHashNavClick(e, "platform");
            }}
            className="body-a-regular block px-5 py-3 text-neutral-800 transition-colors hover:bg-neutral-100"
          >
            Platform
          </a>

          {/* How it works */}
          <a
            href="/#how-it-works"
            onClick={(e) => {
              setMobileMenuOpen(false);
              handleHashNavClick(e, "how-it-works");
            }}
            className="body-a-regular block px-5 py-3 text-neutral-800 transition-colors hover:bg-neutral-100"
          >
            How it works
          </a>

          {/* CTA */}
          <div className="px-5 pt-4 pb-8">
            <Button
              size="sm"
              onClick={() => handleCalendlyClick("header")}
            >
              Schedule a Demo
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
